/* eslint-disable @typescript-eslint/naming-convention */
import {ColDef, ColumnPinnedType, ColumnState, FirstDataRenderedEvent, GridApi, GridState} from 'ag-grid-community';
import {injectable, inject} from 'inversify';
import {makeAutoObservable, runInAction, observe, when, IArrayDidChange} from 'mobx';

import {uiStore} from 'IocContainer';
import {isInstanceOfOperation} from 'modules/Tasks/components/Gantt/utils/gantt';
import {ScheduleTask} from 'modules/Tasks/Views/Schedule/types';
import {IOC_TYPES} from 'shared/models/ioc';
import {TaskObjectType} from 'shared/models/task/const';
import {TaskModelRawDTO} from 'shared/models/task/task';
import {TaskDependencyDto} from 'shared/models/TaskDependency';

import {ScheduleStoreType, StoredState, type TasksStoreType} from './types';

@injectable()
export class ScheduleStore implements ScheduleStoreType {
  @inject(IOC_TYPES.TasksStore) private tasksStore: TasksStoreType;

  disposers: (() => void)[] = [];

  private _gridApi: GridApi | null = null;
  private _idRownumMap: Record<string, string> = {};
  private _sourceMap = new Map<string, TaskDependencyDto[]>();
  private _dependencyRownums = new Map<string, string>();
  private _projectId = '';
  private _hasInitialized = false;
  private _visibleColumns = new Set<string>();
  private _filterIds: string[] | null = null;
  private readonly STORAGE_KEY = 'schedule-grid-state';

  constructor() {
    makeAutoObservable<ScheduleStore, '_gridApi' | 'tasksStore' | 'STORAGE_KEY' | '_filterIds'>(this, {
      _gridApi: false,
      tasksStore: false,
      STORAGE_KEY: false,
      _filterIds: true,
    });
  }

  // -----------------
  // Getters & Basic State Access
  // -----------------
  get initialState(): GridState | undefined {
    try {
      const saved = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}') as StoredState;
      return saved[this._projectId] || undefined;
    } catch {
      return undefined;
    }
  }

  get scheduleTasks(): ScheduleTask[] {
    if (!this._filterIds) {
      return this.tasksStore.tasks;
    }

    const filterIdsSet = new Set(this._filterIds);
    return this.tasksStore.tasks.filter((task) => filterIdsSet.has(task.id));
  }

  get filterIds(): string[] | null {
    return this._filterIds;
  }

  setFilterIds = (ids: string[] | null) => {
    runInAction(() => {
      uiStore.setLoading(true);
      const baseIds = new Set(ids ?? []);
      this._filterIds = Array.from(this.addOrphanedOperationParents(baseIds));
      // Optionally refresh the grid if needed
      if (this._gridApi) {
        this._gridApi.refreshCells({force: true});
      }
      uiStore.setLoading(true);
    });
  };

  private addOrphanedOperationParents(ids: Set<string>): Set<string> {
    const extendedIds = new Set(ids);
    for (const task of this.tasksStore.tasks) {
      if (isInstanceOfOperation(task) && ids.has(task.id) && !ids.has(task.parent_id)) {
        extendedIds.add(task.parent_id);
      }
    }
    return extendedIds;
  }

  get gridApi() {
    return this._gridApi;
  }

  // -----------------
  // Column Management
  // -----------------
  get allColumnIds(): string[] {
    return this._gridApi?.getColumns().map((col) => col.getColId()) ?? [];
  }

  get visibleColumns(): string[] {
    return Array.from(this._visibleColumns);
  }

  get areAllColumnsVisible(): boolean {
    return this.allColumnIds.length > 0 && this.allColumnIds.length === this._visibleColumns.size;
  }

  isColumnVisible(field: string): boolean {
    return this._visibleColumns.has(field);
  }

  private updateVisibleColumnsState = () => {
    if (!this._gridApi) return;
    const visible = this._gridApi
      .getColumns()
      .filter((col) => col.isVisible())
      .map((col) => col.getColId());

    runInAction(() => {
      this._visibleColumns = new Set(visible);
    });
  };

  toggleAllColumns = (visible: boolean) => {
    if (!this._gridApi) return;

    runInAction(() => {
      this._gridApi?.setColumnsVisible(this.allColumnIds, visible);
      this.saveTableState();
      this.updateVisibleColumnsState();
    });
  };

  toggleColumnVisibility = (field: string) => {
    if (!this._gridApi) return;
    const column = this._gridApi.getColumn(field);
    if (column) {
      runInAction(() => {
        const newVisibility = !column.isVisible();
        this._gridApi?.setColumnsVisible([field], newVisibility);
        if (newVisibility) {
          this._visibleColumns.add(field);
        } else {
          this._visibleColumns.delete(field);
        }
        this.saveTableState();
      });
    }
  };

  get hasPartialColumnsVisible(): boolean {
    return this._visibleColumns.size > 0 && !this.areAllColumnsVisible;
  }

  // -----------------
  // Grid State Management
  // -----------------
  setGridApi = (api: GridApi) => {
    this._gridApi = api;

    const syncVisibleColumns = () => {
      this.saveTableState();
    };

    api.addEventListener('gridReady', () => {
      const savedState = this.getSavedState();
      if (savedState?.columnSizing?.columnSizingModel) {
        // Immediately apply column sizing when grid is ready
        api.applyColumnState({
          state: savedState.columnSizing.columnSizingModel,
          applyOrder: false,
        });
      }
    });

    api.addEventListener('firstDataRendered', () => {
      const savedState = this.getSavedState();
      const hasSavedColumns = savedState?.columnVisibility?.hiddenColIds?.length || savedState?.visibleColumns?.length;

      // Apply column sizing again after data is rendered
      if (savedState?.columnSizing?.columnSizingModel) {
        api.applyColumnState({
          state: savedState.columnSizing.columnSizingModel,
          applyOrder: false,
        });
      }

      // Apply column order if it exists
      if (savedState?.columnOrder?.orderedColIds) {
        const columnOrder = savedState.columnOrder.orderedColIds;
        api.applyColumnState({
          state: columnOrder.map((colId: string) => ({
            colId,
            sort: null,
            sortIndex: null,
          })),
          applyOrder: true,
        });
      }

      if (savedState?.columnPinning) {
        const {leftColIds = [], rightColIds = []} = savedState.columnPinning;
        const pinnedState: ColumnState[] = [
          ...leftColIds.map((colId) => ({colId, pinned: 'left' as ColumnPinnedType})),
          ...rightColIds.map((colId) => ({colId, pinned: 'right' as ColumnPinnedType})),
        ];
        api.applyColumnState({
          state: pinnedState,
        });
      }

      if (!hasSavedColumns) {
        const allColIds = api.getColumns()?.map((col) => col.getColId()) ?? [];
        api.setColumnsVisible(allColIds, true);
      }

      if (savedState?.columnVisibility?.hiddenColIds) {
        const visibleCols = this.allColumnIds.filter((id) => !savedState.columnVisibility.hiddenColIds.includes(id));
        api.setColumnsVisible(visibleCols, true);
      }

      this.updateVisibleColumnsState();

      const expanded = new Set(savedState?.expandedRows ?? []);
      api.forEachNode((node) => {
        if (expanded.has(node.data?.id)) node.setExpanded(true);
      });

      // Final application of column sizing to ensure it sticks
      if (savedState?.columnSizing?.columnSizingModel) {
        setTimeout(() => {
          api.applyColumnState({
            state: savedState.columnSizing.columnSizingModel,
            applyOrder: false,
          });
        }, 0);
      }

      this._hasInitialized = true;
    });

    api.addEventListener('columnVisible', syncVisibleColumns);
    api.addEventListener('columnMoved', () => this.saveTableState());
    api.addEventListener('columnPinned', () => this.saveTableState());
    api.addEventListener('columnResized', () => this.saveTableState());
    api.addEventListener('rowGroupOpened', () => this.saveTableState());
  };

  // -----------------
  // State Persistence
  // -----------------
  saveTableState() {
    if (!this._gridApi || !this._hasInitialized) return;

    const expandedRows = new Set<string>();
    this._gridApi.forEachNode((node) => {
      if (node.expanded) expandedRows.add(node.data?.id);
    });

    try {
      const state = this._gridApi.getState();
      const stored = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}') as StoredState;

      // Get the auto-group column's current width
      const autoGroupColumn = this._gridApi.getColumn('ag-Grid-AutoColumn');
      if (autoGroupColumn) {
        // Update or add the auto-group column size in the columnSizing model
        const columnSizingModel = state.columnSizing?.columnSizingModel || [];
        const autoGroupColIndex = columnSizingModel.findIndex((col) => col.colId === 'ag-Grid-AutoColumn');

        const autoGroupColSize = {
          colId: 'ag-Grid-AutoColumn',
          width: autoGroupColumn.getActualWidth(),
          flex: autoGroupColumn.getFlex() || 0,
        };

        if (autoGroupColIndex >= 0) {
          columnSizingModel[autoGroupColIndex] = autoGroupColSize;
        } else {
          columnSizingModel.push(autoGroupColSize);
        }

        state.columnSizing = {
          ...state.columnSizing,
          columnSizingModel,
        };
      }

      stored[this._projectId] = {
        ...state,
        expandedRows: Array.from(expandedRows),
        visibleColumns: this.visibleColumns,
        timestamp: Date.now(),
        autoGroupColumnDef: {
          headerName: 'Activity',
          field: 'name',
          cellRendererParams: {
            suppressCount: true,
            innerRenderer: '(params) => params.value ?? "(unnamed)"',
          },
        },
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(stored));
    } catch (err) {
      console.error('Failed to save grid state:', err);
    }
  }

  getSavedState() {
    try {
      const state = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}') as StoredState;
      return state[this._projectId] || null;
    } catch {
      return null;
    }
  }

  // -----------------
  // Task Management & Dependencies
  // -----------------
  getRowNumber(taskId: string): string {
    return this._idRownumMap[taskId] || '';
  }

  private updateRownumMaps() {
    const idToRownum = new Map<string, string>();
    const filteredTasks = this.tasksStore.tasks?.filter((task) => !!task.outline_sort_key);
    filteredTasks?.sort((a, b) => a.outline_sort_key.localeCompare(b.outline_sort_key));

    let currentTaskNumber = 0;
    let currentLetter = 'A';
    let lastParentNumber = 0;

    filteredTasks?.forEach((task) => {
      if (task.object_type !== TaskObjectType.operation) {
        currentTaskNumber++;
        const taskNumber = currentTaskNumber.toString();
        idToRownum.set(task.id, taskNumber);
        lastParentNumber = currentTaskNumber;
        currentLetter = 'A';
      } else {
        if (!idToRownum.has(task.id)) {
          const rownum = `${lastParentNumber}.${currentLetter}`;
          idToRownum.set(task.id, rownum);
          currentLetter = String.fromCharCode(currentLetter.charCodeAt(0) + 1);
        }
      }
    });

    runInAction(() => {
      this._idRownumMap = Object.fromEntries(idToRownum);
    });
  }

  get autoGroupColumnDef(): ColDef<ScheduleTask> {
    const savedState = this.getSavedState();
    const autoGroupColSize = savedState?.columnSizing?.columnSizingModel?.find(
      (col) => col.colId === 'ag-Grid-AutoColumn',
    );

    return {
      headerName: 'Activity',
      field: 'name' as keyof ScheduleTask,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: (params) => params.value ?? '(unnamed)',
      },
      resizable: true,
      width: autoGroupColSize?.width,
      flex: autoGroupColSize?.flex ?? 1,
      minWidth: 100,
      suppressAutoSize: true,
      suppressSizeToFit: true,
    };
  }

  handleFirstDataRendered = (params: FirstDataRenderedEvent) => {
    const savedState = this.getSavedState();
    const autoGroupColSize = savedState?.columnSizing?.columnSizingModel?.find(
      (col) => col.colId === 'ag-Grid-AutoColumn',
    );

    if (autoGroupColSize?.width) {
      params.api.setColumnWidths([{key: 'ag-Grid-AutoColumn', newWidth: autoGroupColSize.width}]);
    }
  };

  // -----------------
  // Lifecycle & Side Effects
  // -----------------
  initSideEffects = (projectId: string) => {
    this._projectId = projectId;

    const loadDisposer = when(
      () => !this.tasksStore.isLoading && this.tasksStore.tasksLoaded,
      () => {
        this.updateRownumMaps();
        this.linkDependenciesToTasks();
      },
    );

    const taskObserverDisposer = observe(this.tasksStore.tasks, (change) => this.taskListChanged(change));
    const dependencyObserverDisposer = observe(this.tasksStore.dependencies, () => {
      if (this.tasksStore.isLoading || this.tasksStore.silent) return;
      this.linkDependenciesToTasks();
    });

    this.disposers.push(loadDisposer);
    this.disposers.push(taskObserverDisposer);
    this.disposers.push(dependencyObserverDisposer);
  };

  destroySideEffects() {
    while (this.disposers.length) {
      const disposer = this.disposers.pop();
      disposer?.();
    }
    this._filterIds = null;
  }

  // Update onGridPreDestroyed to save the same state structure
  onGridPreDestroyed = (event: {state: GridState}) => {
    if (!this._gridApi) return;

    const expandedRows = new Set<string>();
    this._gridApi.forEachNode((node) => {
      if (node.expanded) expandedRows.add(node.data.id);
    });

    runInAction(() => {
      const stored = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}') as StoredState;
      stored[this._projectId] = {
        ...event.state, // Use the provided grid state
        expandedRows: Array.from(expandedRows),
        visibleColumns: this.visibleColumns,
        timestamp: Date.now(),
        autoGroupColumnDef: {
          headerName: 'Activity',
          field: 'name',
          cellRendererParams: {
            suppressCount: true,
            innerRenderer: '(params) => params.value ?? "(unnamed)"',
          },
        },
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(stored));
    });
  };

  // -----------------
  // Dependency Management
  // -----------------
  private linkDependenciesToTasks() {
    const taskDeps = this.tasksStore.dependencies;
    if (!Array.isArray(taskDeps)) return;

    const sourceMap = new Map<string, TaskDependencyDto[]>();
    const dependencyRownums = new Map<string, string>();

    taskDeps.forEach((dep) => {
      dependencyRownums.set(dep.id, this._idRownumMap[dep.predTaskId]);
      if (!sourceMap.has(dep.taskId)) sourceMap.set(dep.taskId, []);
      sourceMap.get(dep.taskId)?.push(dep);
    });

    runInAction(() => {
      this._sourceMap = sourceMap;
      this._dependencyRownums = dependencyRownums;
    });

    this._gridApi?.refreshCells({
      columns: ['dependenciesFormatted'],
      force: true,
    });
  }

  getFormattedDependencies(taskId: string): string {
    const dependencies = this._sourceMap.get(taskId) || [];
    return dependencies
      .map((dep) => this._dependencyRownums.get(dep.id))
      .filter(Boolean)
      .join(', ');
  }

  private taskListChanged(change: IArrayDidChange<TaskModelRawDTO>) {
    if (this.tasksStore.isLoading || this.tasksStore.silent) return;
    if (!this._gridApi) return;

    // we don't support any other types of updates on static schedules
    if (change.type === 'update') {
      this._gridApi.applyTransaction({update: [change.newValue]});
      this.updateRownumMaps();
      this.linkDependenciesToTasks();
    }

    const expandedRows = this.loadExpandedState();
    if (expandedRows.size > 0) {
      this._gridApi.forEachNode((node) => {
        if (expandedRows.has(node.data.id)) node.setExpanded(true);
      });
    }
  }

  private loadExpandedState(): Set<string> {
    try {
      const state = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}') as StoredState;
      const expandedRows = state[this._projectId]?.expandedRows || [];
      return new Set(expandedRows);
    } catch (error) {
      console.error('Failed to load expanded state:', error);
      return new Set();
    }
  }
}
